import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from 'hooks/auth'
import { useUserRole } from 'hooks/useUserRole'
import { CATEGORY_INTROS, getCategoryDismissedKey, getCategoryFromPathname, getCategorySeenKey, isCategoryIntroPending } from '../utils/categoryIntro'

export const TmsSignupCategoryIntroModal: FC = () => {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { application, user } = useAuth()
    const { role } = useUserRole()
    const [visible, setVisible] = useState(false)

    const category = useMemo(() => getCategoryFromPathname(pathname), [pathname])
    const config = category ? CATEGORY_INTROS[category] : null
    const appId = application?.id ?? ''
    const userId = user?.id ?? ''
    const seenKey = config && appId && userId ? getCategorySeenKey(appId, userId, config.category) : ''
    const dismissedKey = appId && userId ? getCategoryDismissedKey(appId, userId) : ''

    useEffect(() => {
        // Single source of truth for the gate — shared with TmsPageTourOverlay so the two
        // onboarding surfaces never disagree about whether the intro should show.
        if (!config || !isCategoryIntroPending({ pathname, role, appId, userId })) {
            setVisible(false)
            return
        }

        const timer = window.setTimeout(() => setVisible(true), 250)
        return () => window.clearTimeout(timer)
    }, [config, pathname, role, appId, userId])

    const hideForAll = useCallback(() => {
        setVisible(false)
        try {
            if (dismissedKey) localStorage.setItem(dismissedKey, 'true')
        } catch {
            // ignore
        }
    }, [dismissedKey])

    const continueIntoSection = useCallback(() => {
        if (!config) return
        setVisible(false)
        try {
            if (seenKey) localStorage.setItem(seenKey, 'true')
        } catch {
            // ignore
        }
        navigate(config.buttonPath)
    }, [config, navigate, seenKey])

    if (!visible || !config) return null

    return createPortal(
        <div className="fixed inset-0 z-[10090] flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-md" role="presentation">
            <div className="w-full max-w-[560px] overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-start justify-between gap-4 px-6 pt-5">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Get setup</div>
                        <h2 className="mt-3 text-[22px] font-bold leading-tight text-slate-900 dark:text-slate-100">{config.title}</h2>
                    </div>
                    <button
                        type="button"
                        onClick={hideForAll}
                        className="text-sm font-medium text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    >
                        Skip
                    </button>
                </div>

                <div className="px-6 pt-3">
                    <p className="m-0 text-sm leading-6 text-slate-500 dark:text-slate-400">{config.description}</p>

                    <button
                        type="button"
                        onClick={continueIntoSection}
                        className="mt-5 inline-flex h-11 w-full items-center justify-center rounded-full bg-blue-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
                    >
                        {config.buttonLabel}
                    </button>
                </div>

                <div className="mt-5 border-t border-slate-200 dark:border-slate-800 px-6 py-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">How-to video</div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400">Learn more</div>
                    </div>
                    <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-800">
                        <div className="relative" style={{ aspectRatio: '16 / 9' }}>
                            <iframe
                                src={config.videoUrl}
                                title={config.title}
                                className="absolute inset-0 h-full w-full border-0"
                                loading="lazy"
                                allowFullScreen
                                allow="fullscreen; clipboard-write"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}
